/**
 * Alpine host for the location prompts that the server lists in `pending_prompts` (docs#10).
 *
 * The server renders the first prompt kind the web knows into
 * layouts/partials/location-prompt-host.twig. This component shows it at most once per browser
 * tab session. Each action posts the prompt_id of the rendered prompt, so the server refuses an
 * action on a prompt that a newer one replaced.
 */

import apiClient from '../api-client.js';

export const SESSION_KEY = 'locationPrompt:shown';

/** A 403 body that does not arrive in this time counts as no error code, so the prompt stops being busy. */
const ERROR_BODY_TIMEOUT_MS = 5000;

/** True when this tab has not shown a location prompt yet. Claims the slot. */
export function claimSessionSlot(storage) {
    try {
        if (storage.getItem(SESSION_KEY) !== null) {
            return false;
        }
        storage.setItem(SESSION_KEY, '1');
        return true;
    } catch {
        // Without sessionStorage the host cannot keep the once-per-session rule, so it shows nothing.
        return false;
    }
}

/** The `error.code` of an API error body, or null when the body is not one or does not arrive in time. */
async function errorCode(response) {
    let timer;
    const timeout = new Promise((resolve) => {
        timer = setTimeout(() => resolve(null), ERROR_BODY_TIMEOUT_MS);
    });
    const body = response.json();
    // A body that fails after the timeout must not surface as an unhandled rejection.
    body.catch(() => {});
    try {
        return (await Promise.race([body, timeout]))?.error?.code ?? null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
}

/** A null autoCityDefault means the prompt offers no auto_city choice, so Accept sends none. */
export function locationPromptHost(kind, promptId = '', autoCityDefault = null) {
    return {
        kind,
        promptId,
        offersAutoCity: autoCityDefault !== null,
        autoCity: autoCityDefault === true,
        visible: false,
        busy: false,
        closing: false,
        expired: false,
        error: '',

        init() {
            this.visible = claimSessionSlot(window.sessionStorage);
        },

        body(extra = {}) {
            const body = new URLSearchParams();
            if (this.promptId) {
                body.append('prompt_id', this.promptId);
            }
            for (const [key, value] of Object.entries(extra)) {
                body.append(key, value);
            }
            return body;
        },

        /** Close has its own flag, so an action request that never answers cannot keep the prompt open. */
        async dismiss() {
            if (this.closing) {
                return;
            }
            this.closing = true;
            this.visible = false;
            try {
                await apiClient.request(`/location-prompts/${encodeURIComponent(this.kind)}/dismiss`, {
                    method: 'POST',
                    body: this.body(),
                });
            } catch {
                // The prompt stays pending on the server and can show again in a later session.
            } finally {
                this.closing = false;
            }
        },

        accept() {
            return this.act('city_nudge/accept', this.offersAutoCity ? { auto_city: this.autoCity ? 'on' : 'off' } : {});
        },

        undo() {
            return this.act('city_auto_notice/undo', {});
        },

        /** Reloads on success, so the page shows the new city. */
        async act(path, extra) {
            if (this.busy) {
                return;
            }
            const trigger = document.activeElement;
            this.busy = true;
            this.error = '';
            const dataset = this.$el?.dataset ?? {};
            try {
                const response = await apiClient.request(`/location-prompts/${path}`, {
                    method: 'POST',
                    body: this.body(extra),
                });
                if (!this.visible) {
                    // The member closed the prompt while the request ran.
                    this.busy = false;
                    return;
                }
                if (response.ok) {
                    window.location.reload();
                    return;
                }
                // `expired` hides the actions after a 409 or an age contact hold, because neither can
                // succeed in this session. Every other failure stays retryable.
                const held = response.status === 403 && await errorCode(response) === 'age_contact_hold';
                this.expired = response.status === 409 || held;
                if (held) {
                    this.error = dataset.holdMessage ?? '';
                } else if (response.status === 409) {
                    this.error = dataset.expiredMessage ?? '';
                } else {
                    this.error = dataset.errorMessage ?? '';
                }
            } catch {
                this.error = dataset.errorMessage ?? '';
            }
            this.busy = false;
            this.$nextTick(() => this.restoreFocus(trigger));
        },

        /**
         * A disabled or hidden action loses its focus, at a time that differs by browser. An expired
         * prompt has no action left, so the focus goes to Close, which aria-describedby links to the message.
         */
        restoreFocus(trigger) {
            if (!this.visible) {
                return;
            }
            const active = document.activeElement;
            const lost = !active || active === document.body || active === trigger
                || (this.$el.contains(active) && (active.disabled || active.offsetParent === null));
            if (lost) {
                (this.expired ? this.$refs.close : trigger)?.focus();
            }
        },
    };
}
